import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Lightweight, RGPD-friendly analytics.
 * Fires one anonymous pageview per public route change.
 * Admin & api paths are ignored server-side.
 */
export default function PageTracker() {
  const location = useLocation();
  const lastPath = useRef(null);
  const viewId = useRef(null);
  const enteredAt = useRef(null);

  useEffect(() => {
    const path = location.pathname || "/";
    // Scroll to top on route change, OR scroll to the target anchor if the URL has a #hash.
    if (location.hash) {
      const id = location.hash.slice(1);
      // small delay so the target section is in the DOM after the route render
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    }
    if (path.startsWith("/admin")) return;
    if (lastPath.current === path) return;
    lastPath.current = path;

    // Meta Pixel — fire PageView on every SPA route change (index.html only
    // fires the initial one; internal <Link> navigations must be tracked here).
    if (typeof window.fbq === "function") {
      try { window.fbq("track", "PageView"); } catch (_) { /* no-op */ }
    }

    const referrer =
      document.referrer && !document.referrer.includes(window.location.host)
        ? document.referrer
        : "";
    flushLeave();
    enteredAt.current = Date.now();
    viewId.current = null;
    api.post("/track/pageview", { path, referrer })
      .then(r => { viewId.current = r.data?.id || null; })
      .catch(() => {});
  }, [location.pathname, location.hash]); // eslint-disable-line

  // Temps passé sur la page : envoyé quand on quitte la page / l'onglet.
  function flushLeave() {
    if (!viewId.current || !enteredAt.current) return;
    const seconds = Math.round((Date.now() - enteredAt.current) / 1000);
    if (seconds < 1) return;
    const body = JSON.stringify({ id: viewId.current, seconds });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/leave`;
    try {
      if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      else fetch(url, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true });
    } catch (_) { /* no-op */ }
    viewId.current = null;
  }
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flushLeave(); };
    window.addEventListener("pagehide", flushLeave);
    document.addEventListener("visibilitychange", onHide);
    return () => { window.removeEventListener("pagehide", flushLeave); document.removeEventListener("visibilitychange", onHide); };
  }, []); // eslint-disable-line

  return null;
}
